import { useState } from 'react';
import type { IRepoQualityRow } from '@skystone/shared';
import { Badge } from '@/components/ui';
import { useMediaQuery } from '@/hooks/use-media-query';
import { RankedPanelShell } from './RankedPanelShell';
import { RepoQualityRow } from './RepoQualityRow';

interface FailingQualityGatePanelProps {
  rows: IRepoQualityRow[];
  inScopeRepos: number;
}

/**
 * Lists repos at `quality_status = FAILED`, sorted by lastAnalysedAt desc
 * server-side. Renders top-10 on `sm+`, top-5 below to keep mobile vertical
 * scroll bounded.
 */
export function FailingQualityGatePanel({
  rows,
  inScopeRepos,
}: Readonly<FailingQualityGatePanelProps>) {
  const [expanded, setExpanded] = useState(false);
  const isSmUp = useMediaQuery('(min-width: 640px)');
  const defaultLimit = isSmUp ? 10 : 5;
  const visibleRows = expanded ? rows : rows.slice(0, defaultLimit);

  return (
    <RankedPanelShell
      testId="failing-quality-gate-panel"
      title="Failing Quality Gate"
      countLabel={`(${rows.length} of ${inScopeRepos})`}
      metricKey="cq_qg_pass_rate"
      totalRows={rows.length}
      defaultLimit={defaultLimit}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
      emptyState={rows.length === 0 ? 'No repos failing Quality Gate — nice.' : null}
    >
      {visibleRows.map((row) => (
        <RepoQualityRow
          key={row.repositoryId}
          repositoryId={row.repositoryId}
          repositoryName={row.repositoryName}
          projectKey={row.projectKey}
          organization={row.organization}
          lastAnalysedAt={row.lastAnalysedAt}
          valueSlot={<Badge variant="error">FAILED</Badge>}
        />
      ))}
    </RankedPanelShell>
  );
}
